/**
 * Recipient-facing intake-status helpers.
 *
 * Extracted from IntakeForm.tsx so the "is this recipient done?" decision
 * — which drives whether the merchant sees the success view or a still-
 * editable/signable form — is unit-testable (Suite 5.6, the signature-only
 * trap: a merchant who signs an mnspOnly row but closes the tab before
 * submitting must be guided back to finish, not shown as complete).
 */

// Statuses where the intake row has already moved past the recipient's
// editable phase. Once the row is in one of these, the form must show the
// success view rather than an editable form, so a duplicate submission
// can't be made. ERROR is deliberately OUT: an auto-submit that errored
// should let the recipient correct their data and retry. Aligns with the
// server-side "already submitted" list in intakeApiHandler._handle_complete.
// Not used for the mnspOnly flow -- see isRecipientActionComplete below.
export const POST_SUBMIT_STATUSES: ReadonlySet<string> = new Set([
  "READY_FOR_REVIEW",
  "SUBMITTED",
  "SIGNATURE_SENT",
  "IN_REVIEW",
  "NEEDS_INFO",
  "APPROVED",
  "BOARDED",
  "REJECTED",
  "ABANDONED",
]);

/**
 * Whether the recipient's action on the intake page is already complete, so
 * the page should show the success view instead of a signable/editable form.
 *
 * For a normal intake the recipient FILLS the form, so any post-submit
 * status means "done". For an mnspOnly (admin-fill) row the recipient only
 * SIGNS: the admin already submitted the merchant application, so the row
 * arrives at SIGNATURE_SENT (a post-submit status) BEFORE the merchant has
 * signed anything. Using the generic set there would wrongly trap the
 * merchant on the thank-you page so they can never sign.
 *
 * The mnspOnly "done" signal is `mnspEnrollmentSubmittedAt` (its presence,
 * not its value), NOT `status === "MNSP_SIGNED"`. A bundled row's Status
 * deliberately never moves to MNSP_SIGNED on signing -- that status is
 * read-only downstream and would wrongly freeze a bundled application
 * still mid-boarding on CardConnect (the two signatures are independent).
 * MNSP_ENROLLMENT_ONLY rows DO still reach MNSP_SIGNED (nothing else to
 * wait on there), but mnspEnrollmentSubmittedAt is stamped at the same
 * moment either way, so it's a uniform signal for both. It's REMOVEd if an
 * operator edit invalidates a bundled row's MAR and requires a re-sign,
 * and re-stamped once the merchant re-signs -- so it tracks "signed right
 * now" correctly across a resign cycle too.
 */
export function isRecipientActionComplete(
  status: string | null | undefined,
  mnspOnlyRow: boolean,
  mnspEnrollmentSubmittedAt?: string | null,
): boolean {
  if (!status) return false;
  return mnspOnlyRow
    ? !!mnspEnrollmentSubmittedAt
    : POST_SUBMIT_STATUSES.has(status);
}
